export type ExchangeRecoveryVerdictKey =
  | 'current_again'
  | 'stale_verified'
  | 'provider_auth_repair'
  | 'provider_credential_repair'
  | 'provider_replay_window'
  | 'runner_wait'
  | 'never_synced';

export interface ExchangeRecoveryTruth {
  healthState: 'healthy' | 'stale' | 'blocked' | 'never_synced';
  freshnessState?: string | null;
  freshnessBucket?: string | null;
  hasProof: boolean;
  blockerClassification?: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
  credentialActionRequired?: boolean;
  providerAuthActionRequired?: boolean;
  providerReplayBlocked?: boolean;
  runnerWaitState?: 'runner_limited_wait' | null;
}

const RECOVERY_LABELS: Record<ExchangeRecoveryVerdictKey, string> = {
  current_again: 'Current again',
  stale_verified: 'Stale but verified',
  provider_auth_repair: 'Sign in to refresh this exchange',
  provider_credential_repair: 'Repair credentials',
  provider_replay_window: 'Provider replay window',
  runner_wait: 'Waiting for Pinecurl to retry safely',
  never_synced: 'Never synced',
};

const RECOVERY_BADGE_CLASSES: Record<ExchangeRecoveryVerdictKey, string> = {
  current_again: 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  stale_verified: 'border border-amber-500/30 bg-amber-500/10 text-amber-300',
  provider_auth_repair: 'border border-amber-500/30 bg-amber-500/10 text-amber-200',
  provider_credential_repair: 'border border-amber-500/30 bg-amber-500/10 text-amber-200',
  provider_replay_window: 'border border-red-500/30 bg-red-500/10 text-red-300',
  runner_wait: 'border border-blue-500/30 bg-blue-500/10 text-blue-200',
  never_synced: 'border border-blue-500/30 bg-blue-500/10 text-blue-300',
};

export function getExchangeFreshnessRecoveryVerdictKey(
  truth: ExchangeRecoveryTruth,
): ExchangeRecoveryVerdictKey {
  if (!truth.hasProof || truth.healthState === 'never_synced' || truth.freshnessState === 'never_synced' || truth.freshnessBucket === 'never_synced') {
    return 'never_synced';
  }

  if (truth.healthState === 'blocked') {
    if (
      truth.blockerClassification === 'auth_action' ||
      truth.providerAuthActionRequired
    ) {
      return 'provider_auth_repair';
    }

    if (
      truth.blockerClassification === 'credential_action' ||
      truth.credentialActionRequired
    ) {
      return 'provider_credential_repair';
    }

    return 'provider_replay_window';
  }

  if (truth.runnerWaitState) {
    return 'runner_wait';
  }

  if (truth.healthState === 'stale' || truth.freshnessState === 'stale_verified' || truth.freshnessBucket === 'stale') {
    return 'stale_verified';
  }

  return 'current_again';
}

export function getExchangeFreshnessRecoveryLabel(truth: ExchangeRecoveryTruth): string {
  return RECOVERY_LABELS[getExchangeFreshnessRecoveryVerdictKey(truth)];
}

export function getExchangeFreshnessRecoveryBadgeClass(truth: ExchangeRecoveryTruth): string {
  return RECOVERY_BADGE_CLASSES[getExchangeFreshnessRecoveryVerdictKey(truth)];
}
